const API_BASE_URL = process.env.REACT_APP_PATH_TO_API;

export interface UserPreferences {
  enableFaceAnalysis?: boolean;
  enableImageAnalysis?: boolean;
}

/**
 * Updates the user's preferences
 * @param preferences Object containing enableFaceAnalysis and/or enableImageAnalysis flags
 * @returns Promise that resolves with the updated user preferences
 */
export const updateUserPreferences = async (preferences: UserPreferences): Promise<{ userAttributes: UserPreferences }> => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_BASE_URL}/user/preferences`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(preferences)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update user preferences');
    }

    const result = await response.json();
    
    // Update session storage with new values
    if (preferences.enableFaceAnalysis !== undefined) {
      sessionStorage.setItem('enableFaceAnalysis', preferences.enableFaceAnalysis.toString());
    }
    if (preferences.enableImageAnalysis !== undefined) {
      sessionStorage.setItem('enableImageAnalysis', preferences.enableImageAnalysis.toString());
    }
    
    console.log('User preferences updated successfully');
    return result;
  } catch (error) {
    console.error('Error updating user preferences:', error);
    throw error;
  }
};

/**
 * Gets the user's preferences
 * @returns Promise that resolves with the user preferences
 */
export const getUserPreferences = async (): Promise<{ userAttributes: UserPreferences }> => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_BASE_URL}/user/preferences`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get user preferences');
    }

    const result = await response.json();
    
    // Update session storage with retrieved values
    if (result.userAttributes.enableFaceAnalysis !== undefined) {
      sessionStorage.setItem('enableFaceAnalysis', result.userAttributes.enableFaceAnalysis.toString());
    }
    if (result.userAttributes.enableImageAnalysis !== undefined) {
      sessionStorage.setItem('enableImageAnalysis', result.userAttributes.enableImageAnalysis.toString());
    }
    
    return result;
  } catch (error) {
    console.error('Error getting user preferences:', error);
    throw error;
  }
};
